import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Plus } from 'lucide-react';
import { useWidgetGridContext } from '../../context';
import { useWidgetStateCopy } from '../../context/WidgetStateCopyContext.jsx';
import { useWidgetData } from '../../hooks';
import { executeAction } from '../../utils';
import WidgetCardShell from '../WidgetCardShell';
import WidgetErrorBoundary from '../WidgetErrorBoundary';
import WidgetStatePlaceholder, { DEFAULT_STATE_COPY } from '../WidgetStatePlaceholder';
import WidgetSkeleton from '../WidgetSkeleton';
import Widget from '../Widget';
import ActivityListWidget from '../../customWidgets/ucpWidgets/ActivityListWidget';
import NotesListWidget from '../../customWidgets/ucpWidgets/NotesListWidget';
import KpiWidget from '../../customWidgets/ucpWidgets/KpiWidget';
import { COMPRESSED_LAYOUT_W_THRESHOLD } from '../../customWidgets/ucpWidgets/ActivityListWidget/constants';

const CUSTOM_FIXED_REGISTRY = {
  activity: ActivityListWidget,
  notes: NotesListWidget,
  kpi: KpiWidget,
};

const HEADER_ICON_REGISTRY = Object.freeze({
  Plus,
});

const WIDGET_DEFINITION_KEY_MAP = Object.freeze({
  'd1-notes-def': 'notes',
  'd2-activity-def': 'activity',
  'd3-kpi-def': 'kpi',
});

const resolveCustomFixedKey = (widgetConfig) => {
  const explicitKey =
    widgetConfig.custom_fixed_key ||
    widgetConfig.config?.custom_fixed_key ||
    widgetConfig.config?.card?.custom_fixed_key ||
    null;

  if (explicitKey) return explicitKey;
  return WIDGET_DEFINITION_KEY_MAP[widgetConfig.widget_definition_uuid] || null;
};

const WidgetWrapperInner = ({ widgetConfig, layoutW = null }) => {
  const {
    scopeContext,
    dashboardUuid,
    activeFilters,
    onNavigate,
    onWidgetAction,
    onCreateNoteClick,
    customWidgetRegistry,
    dataAdapters,
  } = useWidgetGridContext();

  // The app's copy if it provided any, the package's English otherwise
  // (CT-5885). Same source as `CatalogWidget` and `WidgetNothingToDraw`, so
  // "there is nothing here" reads the same wherever it is decided.
  const stateCopy = useWidgetStateCopy();

  const {
    data,
    loading,
    error,
    isEmpty,
    refetch,
    canRetry,
    containerRef,
  } = useWidgetData({
    instanceUuid: widgetConfig.instance_uuid,
    scopeContext,
    dashboardUuid,
    activeFilters,
    acceptsGlobalFilters: widgetConfig.accepts_global_filters,
    fetchWidgetData: dataAdapters.fetchWidgetData,
  });

  const cardConfig = widgetConfig.config?.card || {};
  const title = cardConfig.title || '';
  const description = cardConfig.description || '';
  const headerButtons = cardConfig.header_buttons || [];
  const resolvedHeaderButtons = useMemo(
    () =>
      headerButtons.map((button) => {
        if (button.icon || !button.iconKey) return button;
        const resolvedIcon = HEADER_ICON_REGISTRY[button.iconKey];
        return resolvedIcon ? { ...button, icon: resolvedIcon } : button;
      }),
    [headerButtons],
  );
  const widgetType = widgetConfig.widget_type;

  const isCompressed = useMemo(
    () => layoutW != null && layoutW <= COMPRESSED_LAYOUT_W_THRESHOLD,
    [layoutW],
  );

  const registry = customWidgetRegistry || CUSTOM_FIXED_REGISTRY;
  const customFixedKey = resolveCustomFixedKey(widgetConfig);

  const actionHandlers = useMemo(
    () => ({
      onNavigate,
      onWidgetAction,
      onRefresh: refetch,
      onSlideout: undefined,
      onPopup: undefined,
      onApiAction: undefined,
    }),
    [onNavigate, onWidgetAction, refetch],
  );

  const handleAction = useCallback(
    (action) => {
      executeAction(action, actionHandlers, { scopeContext });
    },
    [actionHandlers, scopeContext],
  );

  const footerSlot = useMemo(() => {
    const footerButtons = cardConfig.footer_buttons || [];
    if (footerButtons.length === 0) return null;
    return (
      <>
        {footerButtons.map((btn) => (
          <button
            key={btn.key}
            type="button"
            onClick={() => handleAction(btn.action)}
            className="px-3 py-1.5 text-sm font-medium rounded border border-edge-strong bg-surface text-content-secondary hover:bg-surface-hover transition-colors cursor-pointer"
          >
            {btn.label}
          </button>
        ))}
      </>
    );
  }, [cardConfig.footer_buttons, handleAction]);

  const handleNotesItemClick = useCallback(
    (item) => {
      handleAction({ type: 'NAVIGATE', target: `activity?noteId=${item.id}` });
    },
    [handleAction],
  );

  const handleActivityItemClick = useCallback(
    (item) => {
      handleAction({ type: 'NAVIGATE', target: `activity?activityId=${item.id}` });
    },
    [handleAction],
  );

  const handleCreateNote = useCallback(() => {
    if (onCreateNoteClick) {
      onCreateNoteClick();
      return;
    }
    handleAction({ type: 'NAVIGATE', target: 'activity' });
  }, [handleAction, onCreateNoteClick]);

  const renderContent = () => {
    if (loading) return <WidgetSkeleton />;

    if (widgetType === 'CUSTOM_FIXED' && customFixedKey) {
      const WidgetComponent = registry[customFixedKey];
      if (WidgetComponent) {
        const baseProps = {
          data,
          isCompressed,
          error,
          errorCode: error?.code,
          onRetry: refetch,
          canRetry,
        };
        let extraProps = {};
        if (customFixedKey === 'notes') {
          extraProps = {
            onItemClick: handleNotesItemClick,
            onCreate: handleCreateNote,
          };
        } else if (customFixedKey === 'activity') {
          extraProps = { onItemClick: handleActivityItemClick };
        }
        return <WidgetComponent {...baseProps} {...extraProps} />;
      }
    }

    if (error) {
      return (
        <WidgetStatePlaceholder
          error={error}
          errorCode={error?.code}
          stateCopy={stateCopy}
          onRetry={refetch}
          canRetry={canRetry}
        />
      );
    }

    if (isEmpty) {
      return <WidgetStatePlaceholder isEmpty stateCopy={stateCopy} />;
    }

    return <Widget data={data} emptyMessage={stateCopy.EMPTY?.message} />;
  };

  return (
    <div ref={containerRef} className="h-full min-h-0">
      <WidgetErrorBoundary instanceId={widgetConfig.instance_uuid}>
        <WidgetCardShell
          title={title}
          description={description}
          headerButtons={resolvedHeaderButtons}
          onAction={handleAction}
          footerSlot={footerSlot}
        >
          {renderContent()}
        </WidgetCardShell>
      </WidgetErrorBoundary>
    </div>
  );
};

WidgetWrapperInner.propTypes = {
  widgetConfig: PropTypes.shape({
    instance_uuid: PropTypes.string.isRequired,
    widget_type: PropTypes.string.isRequired,
    widget_definition_uuid: PropTypes.string,
    visualization_type: PropTypes.string,
    accepts_global_filters: PropTypes.bool,
    config: PropTypes.object,
  }).isRequired,
  layoutW: PropTypes.number,
};


const WidgetWrapper = memo(WidgetWrapperInner);

export default WidgetWrapper;
